import React from 'react';
import { AlertTriangle, FileText } from 'lucide-react';
import { FlatTask, Priority, TaskTotals } from '../../services/taskList';
import { EmptyState, LinkButton, Panel, PanelHeader } from './dashboardUi';
import { DOT_TONE, Tone } from './dashboardTones';
import { PlanungHandlungsbedarf, type PlanungHandlungsbedarfStand } from './PlanungAmpelPanel';
import type { DashboardProject } from './dashboardModel';

/**
 * Was eine Zeile oeffnet: das Projekt der Aufgabengruppe — so, wie die
 * Aufgabenliste es fuehrt. Die Planungszeilen (DashboardProject) passen
 * ebenfalls hinein, darum genuegt fuer beide Wege dieser eine Typ.
 */
type TaskOpenTarget = FlatTask['group']['project'];

interface Props {
    tasks: ReadonlyArray<FlatTask>;
    totals: TaskTotals;
    limit?: number;
    onOpen: (project: TaskOpenTarget) => void;
    onShowAll: () => void;
    /**
     * Planungsphasen mit Handlungsbedarf (Ampel rot/gelb/fehler) — als
     * Abschnitt unter den Aufgaben, damit die Liste in jeder Vorlage und auf
     * dem Handy steht, wo die Hub-Kachel fehlt. Ohne Angabe wie bisher.
     */
    planung?: PlanungHandlungsbedarfStand;
    /** Oeffnet das Projekt und springt ins Planungsmodul; ohne Angabe nur das Projekt. */
    onOpenPlanung?: (project: DashboardProject) => void;
    ref?: React.Ref<HTMLDivElement>;
}

const PRIO_TONE: Record<Priority, Tone> = { urgent: 'red', soon: 'amber', normal: 'slate' };
const PRIO_LABEL: Record<Priority, string> = { urgent: 'Dringend', soon: 'Bald fällig', normal: 'Offen' };

/**
 * Die dringendsten Aufgaben aus der Aufgabenliste — dieselbe Ableitung
 * (services/taskList.ts), damit Kachel und Liste nie verschiedene Zahlen
 * zeigen. „Alle" springt in die Aufgabenliste.
 */
export const TaskPanel: React.FC<Props> = ({ tasks, totals, limit = 5, onOpen, onShowAll, planung, onOpenPlanung, ref }) => {
    const shown = tasks.slice(0, limit);
    const rest = totals.total - shown.length;
    const planungZeilen = planung?.zeilen.length ?? 0;
    // Der Abschnitt zeichnet sich nur, wenn er etwas zu sagen hat (Zeilen,
    // Laden, Lesefehler) — der leere Zustand „Keine offenen Aufgaben" gilt
    // deshalb nur, wenn auch die Planungsphasen ruhig sind.
    const planungSichtbar = !!planung && (planung.laden || !!planung.fehler || planungZeilen > 0);
    const sub = [
        totals.urgent > 0 ? `${totals.urgent} dringend` : null,
        planungZeilen > 0 ? `Planungsphase ${planungZeilen}` : null,
    ].filter((t): t is string => !!t).join(' · ') || undefined;
    return (
        <Panel ref={ref} data-tutorial="dashboard.aufgaben-kachel" className="scroll-mt-3">
            <PanelHeader
                icon={<FileText />}
                title="Aufgaben"
                count={totals.total}
                countTone={totals.urgent > 0 ? 'red' : totals.soon > 0 ? 'amber' : 'slate'}
                sub={sub}
                actions={<LinkButton onClick={onShowAll}>Alle</LinkButton>}
            />
            {shown.length === 0 ? (
                planungSichtbar
                    ? <p className="px-3.5 py-2 text-[11px] text-slate-500">Keine offenen Aufgaben aus dem LV.</p>
                    : <EmptyState tone="ok" title="Keine offenen Aufgaben" hint="Vorankündigung, Unterlage und Planungsphasen sind erledigt." />
            ) : (
                <ul className="divide-y divide-slate-100">
                    {shown.map(({ group, task }) => (
                        <li key={`${group.project.id}-${task.id}`}>
                            <button
                                type="button"
                                onClick={() => onOpen(group.project)}
                                className="w-full text-left px-3.5 py-2 flex items-start gap-2.5 hover:bg-slate-50 active:bg-slate-100 transition-colors"
                            >
                                <span className={`mt-1.5 w-1.5 h-1.5 rounded-full shrink-0 ${DOT_TONE[PRIO_TONE[task.priority]]}`} aria-hidden="true" />
                                <span className="min-w-0 flex-1">
                                    <span className="flex items-center gap-1.5 min-w-0">
                                        {task.type === 'inspection_overdue' && <AlertTriangle className="w-3 h-3 text-amber-600 shrink-0" />}
                                        <span className="text-xs font-semibold text-slate-800 truncate">{task.label}</span>
                                        <span className={`text-[11px] shrink-0 ${task.priority === 'urgent' ? 'text-red-700 font-semibold' : task.priority === 'soon' ? 'text-amber-700' : 'text-slate-400'}`}>{PRIO_LABEL[task.priority]}</span>
                                    </span>
                                    <span className="block text-[11px] text-slate-500 truncate mt-0.5">
                                        <span className="font-mono">{group.projectNumber}</span> · {group.projectName}
                                    </span>
                                </span>
                            </button>
                        </li>
                    ))}
                </ul>
            )}
            {rest > 0 && (
                <div className="px-3.5 py-2 border-t border-slate-100 text-center">
                    <LinkButton onClick={onShowAll}>{rest} weitere in der Aufgabenliste</LinkButton>
                </div>
            )}
            {planung && (
                <PlanungHandlungsbedarf
                    zeilen={planung.zeilen}
                    laden={planung.laden}
                    fehler={planung.fehler}
                    limit={limit}
                    onOpen={onOpenPlanung ?? onOpen}
                />
            )}
        </Panel>
    );
};
